/* ============================================================
   BB BY LYRAH — Animations & Interactions
   Sparkle Canvas · Scroll Reveals · Parallax · Cursor
   ============================================================ */

(function () {
  'use strict';

  /* ── SPARKLE PARTICLES ── */
  const SparkleCanvas = {
    canvas: null,
    ctx: null,
    particles: [],
    raf: null,
    isMobile: window.innerWidth < 768,

    init() {
      this.canvas = document.getElementById('bb-sparkle-canvas');
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d');
      this.resize();
      this.createParticles();
      this.loop();

      window.addEventListener('resize', () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => {
          this.isMobile = window.innerWidth < 768;
          this.resize();
          this.createParticles();
        }, 250);
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          cancelAnimationFrame(this.raf);
        } else {
          this.loop();
        }
      });
    },

    resize() {
      this.canvas.width  = window.innerWidth;
      this.canvas.height = window.innerHeight;
    },

    createParticles() {
      const count = this.isMobile ? 45 : 120;
      this.particles = [];
      for (let i = 0; i < count; i++) {
        this.particles.push(this.createParticle());
      }
    },

    createParticle(randomPhase = true) {
      /* 60% white, 40% rose-gold tones — matches brand palette */
      const colors = [
        [255, 255, 255],     /* pure white */
        [255, 255, 255],     /* pure white (weighted) */
        [255, 248, 250],     /* warm white */
        [232, 180, 184],     /* rose powder */
        [196, 146, 138],     /* rose gold */
        [212, 165, 116],     /* rose warm */
        [255, 220, 230],     /* soft pink */
      ];
      const c = colors[Math.floor(Math.random() * colors.length)];
      /* ~20% of particles are 4-pointed stars, rest are circles */
      const isStar = Math.random() < 0.22;
      /* Weight 45% of particles to the left 40% of screen (hero text zone) */
      const inLeftZone = Math.random() < 0.45;
      const x = inLeftZone
        ? Math.random() * this.canvas.width * 0.42
        : Math.random() * this.canvas.width;
      return {
        x,
        y:       Math.random() * this.canvas.height,
        /* Stars can be slightly larger */
        r:       isStar ? Math.random() * 2.4 + 1.0 : Math.random() * 1.4 + 0.3,
        r_base:  0,
        rgb:     c,
        phase:   randomPhase ? Math.random() * Math.PI * 2 : 0,
        /* Speed variation — some twinkle faster */
        speed:   Math.random() * 0.018 + 0.005,
        /* Very slow drift */
        vx:      (Math.random() - 0.5) * 0.08,
        vy:      (Math.random() - 0.5) * 0.04,
        isStar,
        opacity: 0,
      };
    },

    drawStar(ctx, x, y, r, color, alpha) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = color;
      ctx.lineWidth   = r * 0.4;
      ctx.lineCap     = 'round';
      /* Cross / 4-pointed star */
      ctx.beginPath();
      ctx.moveTo(x - r, y); ctx.lineTo(x + r, y);
      ctx.moveTo(x, y - r); ctx.lineTo(x, y + r);
      ctx.stroke();
      /* Diagonal arms (shorter) */
      const d = r * 0.55;
      ctx.lineWidth = r * 0.25;
      ctx.beginPath();
      ctx.moveTo(x - d, y - d); ctx.lineTo(x + d, y + d);
      ctx.moveTo(x + d, y - d); ctx.lineTo(x - d, y + d);
      ctx.stroke();
      ctx.restore();
    },

    drawParticle(p) {
      p.phase += p.speed;
      p.opacity = (Math.sin(p.phase) + 1) / 2;

      /* Slow drift */
      p.x += p.vx;
      p.y += p.vy;
      /* Wrap around edges */
      if (p.x < -10) p.x = this.canvas.width + 10;
      if (p.x > this.canvas.width  + 10) p.x = -10;
      if (p.y < -10) p.y = this.canvas.height + 10;
      if (p.y > this.canvas.height + 10) p.y = -10;

      if (p.opacity < 0.03) return;

      const [r, g, b] = p.rgb;
      const col = `rgba(${r},${g},${b},`;

      if (p.isStar && p.opacity > 0.3) {
        this.drawStar(this.ctx, p.x, p.y, p.r, col + p.opacity + ')', p.opacity * 0.9);
        /* Glow halo around bright stars */
        if (p.opacity > 0.65) {
          this.ctx.save();
          this.ctx.globalAlpha = p.opacity * 0.25;
          this.ctx.shadowBlur  = 10;
          this.ctx.shadowColor = col + '1)';
          this.ctx.beginPath();
          this.ctx.arc(p.x, p.y, p.r * 1.8, 0, Math.PI * 2);
          this.ctx.fillStyle = col + (p.opacity * 0.15) + ')';
          this.ctx.fill();
          this.ctx.restore();
        }
      } else {
        this.ctx.save();
        this.ctx.globalAlpha = p.opacity;
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        this.ctx.fillStyle = col + p.opacity + ')';
        this.ctx.fill();
        if (p.r > 1.0 && p.opacity > 0.55) {
          this.ctx.shadowBlur  = 8;
          this.ctx.shadowColor = col + '1)';
          this.ctx.fill();
        }
        this.ctx.restore();
      }
    },

    loop() {
      this.raf = requestAnimationFrame(() => this.loop());
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.particles.forEach(p => this.drawParticle(p));
    },
  };

  /* ── SCROLL REVEAL ── */
  const ScrollReveal = {
    observers: [],

    init() {
      const selectors = [
        '.bb-reveal',
        '.bb-reveal-left',
        '.bb-reveal-right',
        '.bb-reveal-scale',
      ];

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
      );

      selectors.forEach(sel => {
        document.querySelectorAll(sel).forEach(el => observer.observe(el));
      });

      this.observers.push(observer);
    },

    /* Call again after dynamic content loads */
    refresh() {
      this.observers.forEach(obs => obs.disconnect());
      this.observers = [];
      this.init();
    },
  };

  /* ── PARALLAX ── */
  const Parallax = {
    elements: [],
    ticking: false,

    init() {
      this.elements = Array.from(document.querySelectorAll('[data-parallax]'));
      if (!this.elements.length) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (window.innerWidth < 768) return;

      window.addEventListener('scroll', () => {
        if (!this.ticking) {
          requestAnimationFrame(() => {
            this.update();
            this.ticking = false;
          });
          this.ticking = true;
        }
      }, { passive: true });
    },

    update() {
      const scrollY = window.scrollY;
      this.elements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const speed = parseFloat(el.dataset.parallax) || 0.15;
        const offset = (rect.top + scrollY) - scrollY;
        const translate = (scrollY - offset) * speed;
        el.style.transform = `translate3d(0, ${translate}px, 0)`;
      });
    },
  };

  /* ── CUSTOM CURSOR ── */
  const Cursor = {
    cursor: null,
    ring: null,
    mouseX: 0,
    mouseY: 0,
    ringX: 0,
    ringY: 0,

    init() {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      this.cursor = document.getElementById('bb-cursor');
      this.ring   = document.getElementById('bb-cursor-ring');
      if (!this.cursor || !this.ring) return;

      document.addEventListener('mousemove', e => {
        this.mouseX = e.clientX;
        this.mouseY = e.clientY;
        this.cursor.style.left = e.clientX + 'px';
        this.cursor.style.top  = e.clientY + 'px';
      });

      this.smoothRing();
    },

    smoothRing() {
      requestAnimationFrame(() => this.smoothRing());
      const ease = 0.12;
      this.ringX += (this.mouseX - this.ringX) * ease;
      this.ringY += (this.mouseY - this.ringY) * ease;
      if (this.ring) {
        this.ring.style.left = this.ringX + 'px';
        this.ring.style.top  = this.ringY + 'px';
      }
    },
  };

  /* ── HERO IMAGE LOAD ANIMATION ── */
  const HeroMedia = {
    init() {
      const heroImg = document.querySelector('.bb-hero__media');
      if (!heroImg) return;

      if (heroImg.complete) {
        heroImg.classList.add('is-loaded');
      } else {
        heroImg.addEventListener('load', () => heroImg.classList.add('is-loaded'));
      }
    },
  };

  /* ── WORD-BY-WORD REVEAL ── */
  const WordReveal = {
    init() {
      document.querySelectorAll('[data-words]').forEach(el => {
        const original = el.textContent.trim();
        const words = original.split(/\s+/);
        el.innerHTML = words.map(
          w => `<span class="bb-word">${w}</span>`
        ).join(' ');
      });

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const words = entry.target.querySelectorAll('.bb-word');
          words.forEach((w, i) => {
            setTimeout(() => w.classList.add('is-visible'), i * 80);
          });
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.25 });

      document.querySelectorAll('[data-word-reveal]').forEach(el => observer.observe(el));
    },
  };

  /* ── PRODUCT CARD HOVER (image swap fallback) ── */
  const ProductCards = {
    init() {
      document.querySelectorAll('.bb-product-card').forEach(card => {
        const primary = card.querySelector('.bb-product-card__img--primary');
        const hover   = card.querySelector('.bb-product-card__img--hover');
        if (!primary || !hover) return;

        if (!hover.src && hover.dataset.src) {
          card.addEventListener('mouseenter', () => {
            hover.src = hover.dataset.src;
          }, { once: true });
        }
      });
    },
  };

  /* ── AJAX ADD TO CART (product cards on homepage) ── */
  const CardCart = {
    init() {
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('.bb-atc-card');
        if (!btn) return;
        e.preventDefault();

        const variantId = btn.dataset.variantId;
        if (!variantId || btn.disabled) return;

        const original = btn.textContent;
        btn.textContent = 'Adding…';
        btn.disabled    = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
          });
          if (!res.ok) throw new Error(res.statusText);
          const data = await res.json();

          btn.textContent = 'Added ✓';

          /* Open Horizon cart drawer */
          document.dispatchEvent(new CustomEvent('cart:add', {
            bubbles: true,
            detail: { resource: data },
          }));

          setTimeout(() => {
            btn.textContent = original;
            btn.disabled    = false;
          }, 2200);

        } catch {
          btn.textContent = 'Error';
          setTimeout(() => {
            btn.textContent = original;
            btn.disabled    = false;
          }, 2500);
        }
      });
    },
  };

  /* ── HEADER TRANSPARENT ON SCROLL ── */
  const Header = {
    el: null,
    lastY: 0,

    init() {
      this.el = document.querySelector('header-component, .header');
      if (!this.el) return;

      window.addEventListener('scroll', () => {
        const y = window.scrollY;
        if (y > 60) {
          this.el.classList.add('bb-header--scrolled');
        } else {
          this.el.classList.remove('bb-header--scrolled');
        }
        this.lastY = y;
      }, { passive: true });
    },
  };

  /* ── INIT ON DOM READY ── */
  function init() {
    SparkleCanvas.init();
    ScrollReveal.init();
    WordReveal.init();
    Parallax.init();
    Cursor.init();
    HeroMedia.init();
    ProductCards.init();
    CardCart.init();
    Header.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Expose refresh for cart/section changes */
  window.BBAnimations = { ScrollReveal };
})();
